import { Box, Container, Typography } from "@mui/material";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";

const GOLD = "#D97706";

const STEPS = [
  { n: "01", Icon: AssignmentIndOutlinedIcon, accent: "#2563EB", title: "Onboard",    body: "Collect KYC documents, ID data, ownership structures, and source-of-funds for every customer through guided digital forms." },
  { n: "02", Icon: PolicyOutlinedIcon,        accent: GOLD,      title: "Screen",     body: "Automatically match against 200+ PEP and sanctions databases. Configurable match-score thresholds surface only real hits." },
  { n: "03", Icon: SpeedOutlinedIcon,         accent: "#7C3AED", title: "Score Risk", body: "A composite risk score is calculated across nationality, business type, delivery channel, funds origin, and residency status." },
  { n: "04", Icon: SummarizeOutlinedIcon,     accent: "#16A34A", title: "Report",     body: "Generate FIU-ready DPMSR / STR / CTR reports, schedule re-screening, and maintain an audit trail for regulatory examination." },
];

export default function Process() {
  return (
    <Box
      id="how-it-works"
      sx={{
        background: "#fff",
        py: { xs: 10, md: 16 },
        borderTop: "1px solid #E2E8F0",
        borderBottom: "1px solid #E2E8F0",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Subtle gold glow */}
      <Box sx={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%,-50%)",
        width: "50%", height: "50%",
        background: "radial-gradient(ellipse, rgba(217,119,6,.05) 0%, transparent 70%)",
        pointerEvents: "none",
      }} />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ textAlign: "center", mb: 10 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: GOLD, letterSpacing: "3px", textTransform: "uppercase", mb: 2, fontFamily: "IBM Plex Sans, sans-serif" }}>
            How It Works
          </Typography>
          <Typography component="h2" className="font-display" sx={{ fontSize: { xs: 28, md: 46 }, fontWeight: 700, color: "#0F172A", letterSpacing: "-1.5px", lineHeight: 1.1 }}>
            From sign-up to submission
            <br />
            <Box component="span" sx={{ color: "#94A3B8", fontWeight: 400 }}>in four steps</Box>
          </Typography>
        </Box>

        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" },
          gap: 2.5, position: "relative",
        }}>
          {/* Connecting line */}
          <Box sx={{
            display: { xs: "none", lg: "block" },
            position: "absolute",
            top: "52px", left: "13%", right: "13%",
            height: "1px",
            background: "linear-gradient(90deg, transparent, #E2E8F0 20%, #E2E8F0 80%, transparent)",
            zIndex: 0,
          }} />

          {STEPS.map((s, i) => (
            <Box
              key={s.n}
              sx={{
                position: "relative", zIndex: 1,
                background: "#fff",
                border: "1px solid #E2E8F0",
                borderRadius: "16px",
                p: 4,
                boxShadow: "0 1px 4px rgba(15,23,42,.05)",
                transition: "all .25s cubic-bezier(.4,0,.2,1)",
                "&:hover": {
                  borderColor: s.accent,
                  transform: "translateY(-6px)",
                  boxShadow: `0 20px 48px ${s.accent}14`,
                },
              }}
            >
              <Box sx={{ position: "relative", display: "inline-flex", mb: 3 }}>
                <Box sx={{
                  width: 52, height: 52, borderRadius: "14px",
                  background: `${s.accent}10`, border: `1px solid ${s.accent}22`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <s.Icon sx={{ fontSize: 26, color: s.accent }} />
                </Box>
                <Box sx={{
                  position: "absolute", top: -8, right: -8,
                  width: 22, height: 22, borderRadius: "50%",
                  background: s.accent,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10, fontWeight: 700, color: "#fff",
                  fontFamily: "IBM Plex Sans, sans-serif",
                  boxShadow: `0 2px 6px ${s.accent}40`,
                }}>
                  {i + 1}
                </Box>
              </Box>

              <Typography sx={{ fontSize: 11, fontWeight: 600, color: s.accent, letterSpacing: "2px", textTransform: "uppercase", mb: 1.5, fontFamily: "IBM Plex Sans, sans-serif" }}>
                Step {s.n}
              </Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#0F172A", mb: 1.5, lineHeight: 1.3, fontFamily: "IBM Plex Sans, sans-serif" }}>
                {s.title}
              </Typography>
              <Typography sx={{ fontSize: 14, color: "#64748B", lineHeight: 1.8, fontFamily: "IBM Plex Sans, sans-serif" }}>
                {s.body}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
